import { ref } from "vue"
import type { Speaker, Segment, Audio, Track, Chapter } from "@/shared/types"
import { useSpeakerStore } from "@/stores/speaker"
import { formatTime } from "@/shared/utils"

// State
const currentTrack = ref<Track | null>(null)
const playing = ref(false)
const audioSrc = ref<string | null>(null)
const duration = ref<number>(0)
const currentTime = ref<number>(0)
const playbackSpeed = ref<number>(1)
const speedOptions = [
  { value: 0.5, label: "0.5x" },
  { value: 0.75, label: "0.75x" },
  { value: 1, label: "1x" },
  { value: 1.25, label: "1.25x" },
  { value: 1.5, label: "1.5x" },
  { value: 2, label: "2x" },
]
const ended = ref<boolean>(false)
const chapters = ref<Chapter[]>([])
const currentTake = ref<1 | 2>(1)

function mapSegmentsToChapters(
  audio: Audio,
  speakerImagesById: Map<number, string> = new Map(),
  speakerNamesById: Map<number, string> = new Map(),
): Chapter[] {
  if (!audio.segments?.length) return []

  const sortedSegments = [...audio.segments].sort((a, b) => a.order - b.order)
  const durationInSeconds = Number(audio.duration) || 0
  const maxRangeValue = Math.max(
    0,
    ...sortedSegments.map((segment) =>
      Math.max(Number(segment.start) || 0, Number(segment.end) || 0),
    ),
  )

  // Some APIs return segment timings in milliseconds while the player uses seconds.
  const useMilliseconds = durationInSeconds > 0 && maxRangeValue > durationInSeconds * 1.5
  const toSeconds = (value: number) => (useMilliseconds ? value / 1000 : value)

  let fallbackStart = 0

  return sortedSegments
    .map((segment, index) => {
      const startRaw = toSeconds(Number(segment.start) || 0)
      const endRaw = toSeconds(Number(segment.end) || 0)
      const segmentDuration = toSeconds(Number(segment.duration) || 0)

      let start = startRaw > 0 ? startRaw : fallbackStart
      start = Math.max(start, 0)

      if (durationInSeconds > 0) {
        start = Math.min(start, durationInSeconds)
      }

      let end = endRaw > start ? endRaw : 0
      if (end <= start && segmentDuration > 0) {
        end = start + segmentDuration
      }

      if (durationInSeconds > 0) {
        end = Math.min(end, durationInSeconds)
      }

      fallbackStart = Math.max(end, start)

      return {
        start,
        end,
        title: speakerNamesById.get(segment.speaker_id) || segment.speaker?.name || `Speaker ${index + 1}`,
        image: speakerImagesById.get(segment.speaker_id),
      }
    })
    .filter((chapter) => chapter.end > chapter.start)
}

export const useAudioPlayer = () => {
  const appConfig = useAppConfig()
  const hooks = useHook()
  const speakers = computed(() => useSpeakerStore().speakers)
  const currentDt = new Date().toISOString().split('T')[0]

  // Actions
  function setTrack(track: Track) {
    dispatchSeekTo(0)
    currentTrack.value = track
    audioSrc.value = track.src
    playbackSpeed.value = 1
    currentTake.value = 1
  }

  function setTake(take: 1 | 2) {
    if (!currentTrack.value || take === currentTake.value) return

    currentTake.value = take
    const url = new URL(currentTrack.value.src, window.location.origin)
    if (take === 2) {
      url.searchParams.set("take", "2")
    } else {
      url.searchParams.delete("take")
    }
    audioSrc.value = url.pathname + url.search
  }

  function play() {
    if (ended.value) {
      dispatchSeekTo(0)
    }
    playing.value = true
    ended.value = false
  }

  function setAtBeginning() {
    playing.value = false
    ended.value = false
    dispatchSeekTo(0)
  }

  function pause() {
    playing.value = false
  }

  function setDuration(newDuration: number) {
    duration.value = newDuration
  }

  function setCurrentTime(newCurrentTime: number) {
    currentTime.value = newCurrentTime
  }

  function dispatchSeekTo(value: number) {
    hooks.callPlayerSeekTo({ detail: value })
  }

  function setTrackAndPlay(audio: Audio, token: string) {
    const newAudioSrc = `/api/v1/audios/${audio.id}/audio?token=${token}&v=${Date.now()}`

    pause()
    if (!audio) {
      setChapters([])
      return
    }
    if (audio.status != 'completed') {
      setChapters([])
      return
    }

    const title = audio.title.length > 35 ? audio.title.slice(0, 35) + "..." : audio.title
    const segmentSpeakerIDs = audio.segments.map((segment: Segment) => segment.speaker_id)
    const segmentSpeakers = speakers.value.filter((speaker: Speaker) => segmentSpeakerIDs.includes(speaker.id))
    const speakerImagesById = new Map<number, string>(
      segmentSpeakers.map((speaker) => [
        speaker.id,
        `/api/v1/speakers/${speaker.id}/thumbnail?d=${currentDt}`,
      ]),
    )
    const speakerNamesById = new Map<number, string>(
      segmentSpeakers.map((speaker) => [speaker.id, speaker.name]),
    )
    const speakerNames = segmentSpeakers[0]?.name
    const artistPhoto = segmentSpeakers[0]
      ? speakerImagesById.get(segmentSpeakers[0].id)
      : undefined

    setTrack({
      audioId: audio.id,
      title: title,
      artist: speakerNames ?? "",
      artistPhoto: artistPhoto,
      src: newAudioSrc,
      fallbackSrc: newAudioSrc,
    })
    setChapters(mapSegmentsToChapters(audio, speakerImagesById, speakerNamesById))
  
    if (appConfig.editor.autoPlay) {
      dispatchSeekTo(0)
      play()
    } else {
      playing.value = false
      ended.value = false
      dispatchSeekTo(0)
    }
  }

  function end() {
    ended.value = true
  }

  function setChapters(newChapters: Chapter[]) {
    chapters.value = newChapters ?? []
  }

  // Getters
  const formattedTime = computed(() => formatTime(currentTime.value))
  const formattedDuration = computed(() => formatTime(duration.value))

  return {
    // state
    currentTrack,
    playing,
    audioSrc,
    duration,
    currentTime,
    playbackSpeed,
    speedOptions,
    ended,
    chapters,
    currentTake,

    // getters
    formattedTime,
    formattedDuration,

    // actions
    setTrack,
    play,
    setAtBeginning,
    pause,
    setDuration,
    setCurrentTime,
    dispatchSeekTo,
    setTrackAndPlay,
    setTake,
    end,
    setChapters,
  }
}
